// 테마 토글 — 사용자가 환경설정에서 배경색 선택. localStorage 영구 저장.
// 5 옵션: cream (디폴트, Claude warm) / neutral / white / cool / custom (사용자 지정).
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "docuai-theme";
const STORAGE_CUSTOM = "docuai-theme-custom";
const STORAGE_ACCENT = "docuai-theme-accent";
const STORAGE_ACCENT_CUSTOM = "docuai-theme-accent-custom";

export const ACCENT_OPTIONS = [
  {
    id: "strong",
    label: "진함",
    desc: "기본 (Material Blue 100)",
    bg: "#bbdefb",
  },
  {
    id: "custom",
    label: "사용자 지정",
    desc: "Palette 로 직접 선택",
    bg: null,
  },
];
export const THEME_OPTIONS = [
  {
    id: "cream",
    label: "Cream",
    desc: "Claude 풍 따뜻한 베이지",
    swatch: "#fafaf8",
  },
  {
    id: "neutral",
    label: "Neutral",
    desc: "순 회색 (R=G=B)",
    swatch: "#fafafa",
  },
  {
    id: "white",
    label: "White",
    desc: "순백",
    swatch: "#ffffff",
  },
  {
    id: "cool",
    label: "Cool",
    desc: "약간 푸른 톤",
    swatch: "#f8fafc",
  },
  {
    id: "custom",
    label: "사용자 지정",
    desc: "Palette 로 직접 선택 (밝은 색만)",
    swatch: null, // custom 색 동적 swatch
  },
];

// hex (#rrggbb) → {r,g,b}
export function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
function rgbToHex({ r, g, b }) {
  const h = (x) =>
    Math.max(0, Math.min(255, Math.round(x)))
      .toString(16)
      .padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

// 검정 텍스트 (#1a1b1e) 와 충분히 대비되는 밝기인지 (relative luminance > 0.7 권장).
// WCAG relative luminance = 0.2126*R + 0.7152*G + 0.0722*B (정규화 0-1).
export function isLightEnough(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const norm = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * norm(rgb.r) + 0.7152 * norm(rgb.g) + 0.0722 * norm(rgb.b);
  return L >= 0.7; // 밝은 색만
}

function clamp(v) {
  return Math.max(0, Math.min(255, v));
}

// custom 색 기준 elevated/hover/subtle/border 자동 파생
function deriveCustomTokens(hex) {
  const rgb = hexToRgb(hex) || { r: 250, g: 250, b: 248 };
  return {
    "--audit-bg": hex,
    "--audit-bg-elevated": rgbToHex({
      r: clamp(rgb.r + 6),
      g: clamp(rgb.g + 6),
      b: clamp(rgb.b + 6),
    }),
    "--audit-bg-hover": rgbToHex({
      r: clamp(rgb.r - 10),
      g: clamp(rgb.g - 10),
      b: clamp(rgb.b - 10),
    }),
    "--audit-bg-subtle": rgbToHex({
      r: clamp(rgb.r - 5),
      g: clamp(rgb.g - 5),
      b: clamp(rgb.b - 5),
    }),
    "--audit-border": rgbToHex({
      r: clamp(rgb.r - 20),
      g: clamp(rgb.g - 20),
      b: clamp(rgb.b - 20),
    }),
  };
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && THEME_OPTIONS.some((o) => o.id === v)) return v;
  } catch {}
  return "cream";
}

export function readCustomColor() {
  try {
    const v = localStorage.getItem(STORAGE_CUSTOM);
    if (v && isLightEnough(v)) return v;
  } catch {}
  return "#fafaf8"; // 디폴트 cream
}

export function readAccent() {
  try {
    const v = localStorage.getItem(STORAGE_ACCENT);
    if (v && ACCENT_OPTIONS.some((o) => o.id === v)) return v;
  } catch {}
  return "strong";
}

export function readAccentCustom() {
  try {
    const v = localStorage.getItem(STORAGE_ACCENT_CUSTOM);
    if (v && /^#[0-9a-f]{6}$/i.test(v)) return v;
  } catch {}
  return "#bbdefb";
}

function applyAccent(id, customHex) {
  const opt = ACCENT_OPTIONS.find((o) => o.id === id) || ACCENT_OPTIONS[0];
  const bg = id === "custom" ? customHex || readAccentCustom() : opt.bg;
  document.documentElement.style.setProperty("--audit-accent-bg", bg);
}

function clearCustomStyles() {
  const s = document.documentElement.style;
  s.removeProperty("--audit-bg");
  s.removeProperty("--audit-bg-elevated");
  s.removeProperty("--audit-bg-hover");
  s.removeProperty("--audit-bg-subtle");
  s.removeProperty("--audit-border");
}

function applyTheme(id, customColor) {
  if (id === "cream") {
    document.documentElement.removeAttribute("data-theme");
    clearCustomStyles();
  } else if (id === "custom") {
    document.documentElement.setAttribute("data-theme", "custom");
    const tokens = deriveCustomTokens(customColor || readCustomColor());
    const s = document.documentElement.style;
    for (const [k, v] of Object.entries(tokens)) s.setProperty(k, v);
  } else {
    document.documentElement.setAttribute("data-theme", id);
    clearCustomStyles();
  }
}

// page mount 시 한 번 호출 (App entry point) — localStorage 에서 load + apply.
export function initTheme() {
  const id = readStored();
  applyTheme(id, id === "custom" ? readCustomColor() : null);
  applyAccent(readAccent(), readAccentCustom());
}

export function useTheme() {
  const [theme, setThemeState] = useState(readStored);
  const [customColor, setCustomColorState] = useState(readCustomColor);
  const [accent, setAccentState] = useState(readAccent);
  const [accentCustom, setAccentCustomState] = useState(readAccentCustom);
  useEffect(() => {
    applyTheme(theme, customColor);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      if (theme === "custom") localStorage.setItem(STORAGE_CUSTOM, customColor);
    } catch {}
  }, [theme, customColor]);
  useEffect(() => {
    applyAccent(accent, accentCustom);
    try {
      localStorage.setItem(STORAGE_ACCENT, accent);
      if (accent === "custom")
        localStorage.setItem(STORAGE_ACCENT_CUSTOM, accentCustom);
    } catch {}
  }, [accent, accentCustom]);
  const setTheme = useCallback((id) => {
    if (THEME_OPTIONS.some((o) => o.id === id)) setThemeState(id);
  }, []);
  const setCustomColor = useCallback((hex) => {
    if (!isLightEnough(hex)) return false;
    setCustomColorState(hex);
    setThemeState("custom");
    return true;
  }, []);
  const setAccent = useCallback((id) => {
    if (ACCENT_OPTIONS.some((o) => o.id === id)) setAccentState(id);
  }, []);
  const setAccentCustom = useCallback((hex) => {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return false;
    setAccentCustomState(hex);
    setAccentState("custom");
    return true;
  }, []);
  return {
    theme,
    setTheme,
    customColor,
    setCustomColor,
    accent,
    setAccent,
    accentCustom,
    setAccentCustom,
  };
}
